import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetCustomerPersonasQuery, useCreateCustomerPersonaMutation } from './customerPersonaApiSlice';
import { selectCurrentUser } from '../auth/authSlice';
import { PAGE_SIZE } from '../../constants';
import { Modal, Pill, fieldStyle } from '../../components/adminUi';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  BandButton, ChipRow, Dash, ListPageShell, ListPagination, ListTable, RowAction, Tag, cardAction,
} from '../../components/listPage';

/** 고객 페르소나 목록. A row opens the persona's own page (/customerpersonas/:id); the side
 *  pane it replaced squeezed the table into 10/16 of the width. */

// Drawn with adminUi's Modal, like the other admin forms.
const AddCustomerPersonaModal = ({ open, onClose }) => {
  const [addCustomerPersona] = useCreateCustomerPersonaMutation();
  const [purpose, setPurpose] = useState('');
  const [saving, setSaving] = useState(false);
  const create = async () => {
    setSaving(true);
    try {
      await addCustomerPersona(purpose).unwrap();
      setPurpose('');
      onClose();
    } catch (err) {
      console.log('failed to create new persona');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 고객 페르소나"
      width={520}
      footer={
        <>
          <Pill variant="ghost" onClick={onClose} disabled={saving}>취소</Pill>
          <Pill variant="primary" onClick={create} disabled={saving || !purpose.trim()}>{saving ? '생성 중…' : '생성'}</Pill>
        </>
      }
    >
      <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#86868b' }}>새로운 페르소나의 방문 목적이나 특징을 자유롭게 입력해 주세요.</p>
      <textarea
        autoFocus
        placeholder="예: iPhone 17 Pro Max 구매를 고민 중인 30대 직장인…"
        value={purpose}
        onChange={e => setPurpose(e.target.value)}
        style={{ ...fieldStyle, minHeight: '120px', resize: 'vertical' }}
      />
    </Modal>
  );
};

const CustomerPersonasList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const queryParams = new URLSearchParams(location.search);
  const page = Number(queryParams.get('page') || 1);
  const page_size = Number(queryParams.get('page_size') || PAGE_SIZE);
  const currentUser = useSelector(selectCurrentUser);
  const isStaff = currentUser?.is_staff === true;

  // The 사용자 필터 is gone with dynamic personas, so scope no longer varies by selection.
  // Non-staff keep `user_only` — the route admits 파트너 관리자, and `scope=all` would 403
  // them rather than showing an empty list.
  const scope = isStaff ? 'all' : 'user_only';
  const { data, isLoading } = useGetCustomerPersonasQuery({ page, page_size, scope }, { pollingInterval: 5000 });
  const [addOpen, setAddOpen] = useState(false);

  const setPage = pg => {
    const p = new URLSearchParams(location.search);
    p.set('page', pg);
    navigate(`${location.pathname}?${p.toString()}`);
  };
  const sessions = p => navigate(`/chatsessionslist?page=1&page_size=${PAGE_SIZE}&persona=${p.id}`);
  const label = p => (p.int_id ? `${p.int_id}번 고객` : p.name);
  // Assignment is exclusive, so an active shared persona with no 파트너 is seen by no trainee.
  // It is set on each 파트너's page (파트너 상세 › 고객 페르소나), not here.
  const partners = p => {
    const cs = p.contractors_info || [];
    if (cs.length) return <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{cs.map(c => <Tag key={c.id}>{c.name}</Tag>)}</div>;
    return p.is_dynamic || p.deactivated_at ? <Dash /> : <Tag tone="amber">미배정</Tag>;
  };

  return (
    <ListPageShell
      isMobile={isMobile}
      icon="address card outline"
      title="고객 페르소나 목록"
      subtitle="관리 및 상세 정보 확인"
      actions={<BandButton primary compact={isMobile} icon="plus" onClick={() => setAddOpen(true)}>새 페르소나</BandButton>}
    >
      <ChipRow groups={[]} count={data?.count} />
      <ListTable
        isMobile={isMobile}
        loading={isLoading}
        rows={data?.results || []}
        empty="페르소나가 없습니다."
        onRowClick={p => navigate(`/customerpersonas/${p.id}`)}
        columns={[
          // 번호 is the only persona identifier the trainee and the client use; the pk is one
          // click away on the persona's page.
          { key: 'no', label: '번호', nowrap: true, render: p => <span style={{ fontWeight: 600 }}>{label(p)}</span> },
          { key: 'purpose', label: '설명', render: p => <div title={p.purpose_of_visit} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '640px', color: '#424245' }}>{p.purpose_of_visit}</div> },
          { key: 'status', label: '상태', render: p => (p.deactivated_at ? <Tag tone="gray">비활성</Tag> : <Tag tone="green">사용 중</Tag>) },
          { key: 'sessions', label: '롤플레이 수', align: 'center', render: p => p.count_chatsession },
          { key: 'contractors', label: '파트너', render: partners },
          { key: 'actions', label: '', align: 'right', render: p => <RowAction icon="comments outline" title="롤플레이 기록" onClick={() => sessions(p)} /> },
        ].filter(Boolean)}
        card={{
          title: label,
          tags: p => [p.deactivated_at ? <Tag key="s" tone="gray">비활성</Tag> : <Tag key="s" tone="green">사용 중</Tag>],
          meta: p => [`롤플레이 ${p.count_chatsession}개`, (p.contractors_info || []).map(c => c.name).join(', ') || '파트너 없음'],
          body: p => p.purpose_of_visit,
          actions: p => [<button key="s" type="button" style={cardAction(false, true)} onClick={() => sessions(p)}>롤플레이 기록</button>],
        }}
      />
      <ListPagination page={page} count={data?.count || 0} pageSize={page_size} onChange={setPage} />
      <AddCustomerPersonaModal open={addOpen} onClose={() => setAddOpen(false)} />
    </ListPageShell>
  );
};

export default CustomerPersonasList;
